import React from 'react';
import { AppLink } from 'mydish-ui-util';
import CampfireButton from './CampfireButton';
import TopNavigation, { Navigation, SelectedNavigation } from './TopNavigation';
import Anchor from './Anchor';
import Footer from './Footer';
import '../css/error-boundary-fallback.sass';
import privacyChoices from '../css/assets/img/privacy-choices.png';

interface ErrorBoundaryFallbackProps {
  contactUsLinkLbl?: string;
  contactUsLinkUrl?: string;
  reloadPageBtnLbl?: string;
  payYourBillLinkLbl?: string;
  payYourBillLinkUrl?: string;
  programmingLinkLbl?: string;
  programmingLinkUrl?: string;
  troubleshootLinkLbl?: string;
  troubleshootLinkUrl?: string;
  navigations?: Navigation[];
  selectedNavigation?: SelectedNavigation;
  topNavlogoUrl: string;
  footerLinksContent?: React.ReactNode;
  navigationTheme?: 'day' | 'night';
  copyRightMsg?: React.ReactNode;
  footerLogoUrl?: string;
}
export default function ErrorBoundaryFallback(
  props: ErrorBoundaryFallbackProps
) {
  const {
    contactUsLinkLbl = ` contact us `,
    contactUsLinkUrl = '/support/contact',
    reloadPageBtnLbl = 'Reload Page',
    payYourBillLinkLbl = 'Pay Your Bill',
    payYourBillLinkUrl = '/account/payyourbill',
    programmingLinkLbl = 'Explore Programming',
    programmingLinkUrl = '/upgrades',
    troubleshootLinkLbl = 'Troubleshooting',
    troubleshootLinkUrl = '/support/tech-support',
    navigations = [
      {
        name: 'My Account',
        pageName: 'My Account',
        pageURL: '/account/summary'
      },
      {
        name: 'Perks',
        pageName: 'Perks',
        pageURL: '/perks'
      },
      {
        name: 'Upgrades',
        pageName: 'Upgrades',
        pageURL: '/upgrades'
      },
      {
        name: 'PPV & On Demand',
        pageName: 'PPV & On Demand',
        pageURL: '/payperview'
      },
      {
        name: 'Support',
        pageName: 'Support',
        pageURL: '/support'
      }
    ],
    selectedNavigation = { topNav: 'My Account', drawerNav: 'Login' },
    footerLinksContent = (
      <div className="links" role="navigation" aria-label="Footer Links">
        <AppLink href="https://about.dish.com" className="secondary day">
          <span>About DISH</span>
        </AppLink>
        <AppLink href="https://careers.dish.com" className="secondary day">
          <span>Careers</span>
        </AppLink>
        <AppLink href="https://ir.dish.com" className="secondary day">
          <span>Investor Relations</span>
        </AppLink>
        <AppLink
          href="https://wwwprod.dish.com/service-agreements"
          className="secondary day"
        >
          <span>Legal</span>
        </AppLink>
        <AppLink href="/support/accessibility" className="secondary day">
          <span>Accessibility</span>
        </AppLink>
        <AppLink href="/privacy-notice" className="secondary day">
          <span>Privacy Notice</span>
        </AppLink>
        <AppLink href="/privacy-choices" className="secondary day">
          <span>Your Privacy Choices</span>
          <img src={privacyChoices} alt="" role="presentation" />
        </AppLink>
      </div>
    ),
    navigationTheme = 'night',
    copyRightMsg = <>&copy; 2023, DISH Network L.L.C. All rights reserved.</>
  } = props;

  return (
    <div className="error-boundary-fallback">
      <TopNavigation
        navigations={navigations}
        navEvergreenLinks={[]}
        selectedNavigation={selectedNavigation}
        logo={props.topNavlogoUrl}
        enablePrimaryLinkNavigation
        theme={navigationTheme}
      />
      <div className="contentContainer" id="pageContent">
        <div className="container day">
          <div className="full-width-panel" id="error-msg">
            <div className="container">
              <h1 className="main-heading">Something went wrong</h1>
              <p>
                Oops! It seems something didn&#39;t load quite right. No stress
                – we&#39;re working on fixing it. Please reload the page or
                <Anchor id="contactUsLink" href={contactUsLinkUrl}>
                  {contactUsLinkLbl}
                </Anchor>
                if you need assistance.
              </p>
              <div className="my-3">
                <CampfireButton
                  name="reloadPage"
                  id="reloadPage"
                  label={reloadPageBtnLbl}
                  onClick={() => {
                    window.location.reload();
                  }}
                />
              </div>
            </div>
          </div>
          <div className="full-width-panel">
            <div className="container">
              <div className="row links-section">
                <div className="col-md-8">
                  <h2 className="heading">
                    Were you looking for one of these?
                  </h2>
                  <div className="supportlink">
                    <Anchor id="payYourBillLink" href={payYourBillLinkUrl}>
                      {payYourBillLinkLbl}
                    </Anchor>
                  </div>
                  <div className="supportlink">
                    <Anchor id="programmingLink" href={programmingLinkUrl}>
                      {programmingLinkLbl}
                    </Anchor>
                  </div>
                  <div className="supportlink">
                    <Anchor id="troubleshootLink" href={troubleshootLinkUrl}>
                      {troubleshootLinkLbl}
                    </Anchor>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer
          footerLinksContent={footerLinksContent}
          theme={navigationTheme}
          copyRight={copyRightMsg}
          footerLogoUrl={props.footerLogoUrl}
        />
      </div>
    </div>
  );
}
